import { useState } from 'react'
import { useI18n } from '@/lib/i18n'
import type { EventConfig } from '@/types'
import { QrCode, Mail, MessageSquare, Images, Send, CheckCircle2, AlertTriangle, ChevronDown, ChevronUp } from 'lucide-react'

function ToggleCard({ label, description, icon: Icon, iconColor, checked, onChange, children }: {
  label: string; description: string; icon: React.ElementType; iconColor: string;
  checked: boolean; onChange: (v: boolean) => void; children?: React.ReactNode
}) {
  return (
    <div className={`rounded-xl border transition-colors ${checked ? 'border-primary/30 bg-primary/5' : 'border-border bg-card/50'}`}>
      <label className="flex items-start gap-4 p-4 cursor-pointer">
        <div className={`mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg ${checked ? 'bg-primary/20' : 'bg-muted'}`}>
          <Icon className={`h-4 w-4 ${checked ? iconColor : 'text-muted-foreground'}`} />
        </div>
        <div className="flex-1">
          <div className="flex items-center justify-between">
            <div className="text-sm font-semibold">{label}</div>
            <input type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} />
          </div>
          <div className="text-xs text-muted-foreground mt-0.5">{description}</div>
        </div>
      </label>
      {checked && children && (
        <div className="border-t border-border/60 p-4 space-y-3">
          {children}
        </div>
      )}
    </div>
  )
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="space-y-1">
      <div className="text-xs font-medium text-muted-foreground">{label}</div>
      {children}
    </div>
  )
}

export default function SharingSettings({ event, onChange }: { event: EventConfig; onChange: (u: Partial<EventConfig>) => void }) {
  const { t } = useI18n()
  const { sharing } = event
  const [testStatus, setTestStatus] = useState<'idle' | 'sending' | 'ok' | 'fail'>('idle')
  const [showSmtp, setShowSmtp] = useState(false)

  const update = (key: keyof typeof sharing, value: unknown) => {
    onChange({ sharing: { ...sharing, [key]: value } })
  }

  const sendTestEmail = async () => {
    setTestStatus('sending')
    try {
      const res = await fetch('/api/email/test', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ smtp: sharing.emailSmtp }),
      })
      setTestStatus(res.ok ? 'ok' : 'fail')
    } catch {
      setTestStatus('fail')
    }
    setTimeout(() => setTestStatus('idle'), 4000)
  }

  const inputCls = 'w-full rounded-lg border border-input bg-background px-3 py-2 text-sm text-foreground focus:border-primary/50 transition-colors'

  return (
    <div className="space-y-8 max-w-2xl">
      <div>
        <h2 className="text-xl font-bold">{t('editor.sharing')}</h2>
        <p className="text-sm text-muted-foreground mt-1">Configure how guests can receive and share their photos.</p>
      </div>

      <div className="space-y-3">
        {/* QR */}
        <ToggleCard
          label={t('sharing.qr')}
          description={t('sharing.qrDesc')}
          icon={QrCode}
          iconColor="text-blue-400"
          checked={sharing.qrEnabled}
          onChange={(v) => update('qrEnabled', v)}
        />

        {/* Gallery */}
        <ToggleCard
          label={t('sharing.gallery')}
          description={t('sharing.galleryDesc')}
          icon={Images}
          iconColor="text-purple-400"
          checked={sharing.galleryEnabled}
          onChange={(v) => update('galleryEnabled', v)}
        />

        {/* Email */}
        <ToggleCard
          label={t('sharing.email')}
          description={t('sharing.emailDesc')}
          icon={Mail}
          iconColor="text-emerald-400"
          checked={sharing.emailEnabled}
          onChange={(v) => update('emailEnabled', v)}
        >
          <button
            onClick={() => setShowSmtp(!showSmtp)}
            className="flex w-full items-center justify-between rounded-lg bg-muted px-3 py-2 text-xs font-medium hover:bg-muted/80 transition-colors"
          >
            {t('sharing.smtpConfig')}
            {showSmtp ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
          </button>

          {showSmtp && (
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <Field label={t('sharing.host')}>
                  <input type="text" value={sharing.emailSmtp?.host || ''} onChange={(e) => update('emailSmtp', { ...sharing.emailSmtp, host: e.target.value })} className={inputCls} placeholder="smtp.example.com" />
                </Field>
                <Field label={t('sharing.port')}>
                  <input type="number" value={sharing.emailSmtp?.port || 587} onChange={(e) => update('emailSmtp', { ...sharing.emailSmtp, port: Number(e.target.value) })} className={inputCls} />
                </Field>
                <Field label={t('sharing.user')}>
                  <input type="text" value={sharing.emailSmtp?.user || ''} onChange={(e) => update('emailSmtp', { ...sharing.emailSmtp, user: e.target.value })} className={inputCls} placeholder="user@example.com" />
                </Field>
                <Field label={t('sharing.pass')}>
                  <input type="password" value={sharing.emailSmtp?.pass || ''} onChange={(e) => update('emailSmtp', { ...sharing.emailSmtp, pass: e.target.value })} className={inputCls} />
                </Field>
                <Field label={t('sharing.from')}>
                  <input type="email" value={sharing.emailSmtp?.from || ''} onChange={(e) => update('emailSmtp', { ...sharing.emailSmtp, from: e.target.value })} className={inputCls} placeholder="booth@yourcompany.com" />
                </Field>
              </div>
              <label className="flex items-center gap-3 cursor-pointer rounded-lg border border-border bg-background px-3 py-2 hover:bg-muted transition-colors">
                <input type="checkbox" checked={sharing.emailSmtp?.secure || false} onChange={(e) => update('emailSmtp', { ...sharing.emailSmtp, secure: e.target.checked })} />
                <span className="text-sm">{t('sharing.secure')}</span>
              </label>

              {/* Test button */}
              <button
                onClick={sendTestEmail}
                disabled={testStatus === 'sending'}
                className="flex items-center gap-2 rounded-lg bg-secondary px-4 py-2 text-sm font-medium hover:bg-secondary/80 disabled:opacity-50 transition-colors"
              >
                {testStatus === 'sending' ? (
                  <><div className="h-3.5 w-3.5 animate-spin rounded-full border border-border border-t-foreground" /> {t('sharing.sendingTest')}</>
                ) : testStatus === 'ok' ? (
                  <><CheckCircle2 className="h-3.5 w-3.5 text-green-400" /> {t('sharing.testSent')}</>
                ) : testStatus === 'fail' ? (
                  <><AlertTriangle className="h-3.5 w-3.5 text-destructive" /> {t('sharing.testFailed')}</>
                ) : (
                  <><Send className="h-3.5 w-3.5" /> {t('sharing.testEmail')}</>
                )}
              </button>
            </div>
          )}
        </ToggleCard>

        {/* SMS */}
        <ToggleCard
          label={t('sharing.sms')}
          description={t('sharing.smsDesc')}
          icon={MessageSquare}
          iconColor="text-amber-400"
          checked={sharing.smsEnabled}
          onChange={(v) => update('smsEnabled', v)}
        >
          <p className="text-xs text-muted-foreground">
            Configure Twilio credentials via environment variables: <code className="rounded bg-muted px-1 py-0.5 font-mono text-[11px]">TWILIO_SID</code>, <code className="rounded bg-muted px-1 py-0.5 font-mono text-[11px]">TWILIO_TOKEN</code>, <code className="rounded bg-muted px-1 py-0.5 font-mono text-[11px]">TWILIO_FROM</code>
          </p>
        </ToggleCard>
      </div>
    </div>
  )
}
